import { useEffect, useMemo, useRef, useState } from "react";

import { AgGridReact } from "ag-grid-react";
import { Box } from "@mui/material";

import { useDispatch } from "react-redux";
import { withTranslation } from "react-i18next";
import { cloneDeep, isEmpty, isEqual, isNull } from "lodash";

import ExcelExportButton from "@plannel/components/ExcelExportButton";
import Snackbar from "@plannel/components/Snackbar";
import { SettingsButton } from "@plannel/components/ActionIconButton";
import DefaultGridSetting from "@plannel/components/aggrid/DefaultGridSetting";
import GridUtils from "@plannel/components/aggrid/GridUtils";
import reduxUtil from "@plannel/utils/redux-util";
import { useMenuAccess } from "@plannel/hooks/useMenuAccess";
import debug from "@plannel/utils/debug-util";
import ColorUtil from "@plannel/pages/utils/color-util";
import CodeUtil from "@plannel/pages/utils/code-util";
import DimensionModal from "@plannel/components/modal/DimensionModal";

import productionExceptionsService from "@plannel/services/mp/production-exceptions-service";

import { MPItemAutocomplete } from "@plannel/components/filter/ItemAutocomplete";
import ItemHrchyTreeFilter from "@plannel/components/filter/ItemHrchyTreeFilter";
import FilterContainer from "@plannel/components/layout/FilterContainer";
import MpVersionFilter from "@plannel/components/filter/MpVersionFilter";
import { MPLocationTreeFilter } from "@plannel/components/filter/LocationTreeFilter";

const { snackBarMessageColor } = ColorUtil;
const ProductionExceptionsDetail = ({ t, viewName, title }) => {
  const reduxViewState = reduxUtil.getViewState(viewName);
  const reduxDispatch = useDispatch();
  const { isPermitted } = useMenuAccess();

  const [mpVersionFilter, setMpVersionFilter] = useState(reduxViewState?.mpVersion ? reduxViewState.mpVersion : null);
  const [filterValue, setFilterValue] = useState(reduxViewState?.filter ?? { locations: null, category: null });
  const [invExceptionsDetail, setInvExceptionsDetail] = useState([]);
  const [snackInfo, setSnackInfo] = useState({ open: false, severity: "", content: "" });

  const { versionStatusCode } = CodeUtil;
  const { RELEASED, PLANNED } = versionStatusCode;

  const gridRef = useRef();
  const columnDefs = useRef();
  const bucketColumnDefs = useRef([]);
  const itemSearch = useRef(reduxViewState?.itemSearch ?? "");
  const extraItemCodesRef = useRef(reduxViewState?.extraItemCodes ?? []);

  const [dimensionModalOpen, setDimensionModalOpen] = useState(false);
  const [dimensions, setDimensions] = useState([]);
  const allDimensions = ["itemHrchyNmGrp", "itemCd", "itemNm"];
  const headerNameByField ={"itemHrchyNmGrp": "itemHrchy", "itemCd": "itemCd", "itemNm": "itemName"};
  const requiredDimensions = ["locNmGrp", "locNm"];

  const defaultGridMemo = useMemo(() => {
    return DefaultGridSetting({ title, viewName });

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const defaultColumnDefs = [
    {
      headerName: 'location', colId: "locNmGrp", width: 150, pinned: "left", lockPinned: true, showRowGroup: "locNm",
      type: ["nonEditableColumn"], cellRenderer: 'agGroupCellRenderer', cellRendererParams: { suppressCount: true }
    },
    {
      headerName: "itemHrchy", colId: "itemHrchyNmGrp", width: 150, pinned: "left", lockPinned: true, showRowGroup: 'itemHrchyNm',
      type: ["nonEditableColumn"], cellRenderer: 'agGroupCellRenderer', cellRendererParams: { suppressCount: true }
    },
    { headerName: "locNm", field: "locNm", rowGroup: true, hide: true, suppressColumnsToolPanel: true },
    { headerName: "categoryNm", field: "itemHrchyNm", rowGroup: true, hide: true, suppressColumnsToolPanel: true },
    { headerName: "itemCd", field: "itemCd", type: ["nonEditableColumn"], width: 180, pinned: "left", lockPinned: true, cellClass: 'stringType' },
    { headerName: "itemName", field: "itemNm", type: ["nonEditableColumn"], width: 180, pinned: "left", lockPinned: true, cellClass: 'stringType' }
  ];

  const defaultExportColumnDefs = [
    { field: "locName", colId: "locNmGrp", rowGroup: true, hide: true },
    { field: "itemHrchy", colId: "itemHrchyNmGrp", rowGroup: true, hide: true },
    { headerName: "location", field: "locNm", width: 140 },
    { headerName: "itemHrchy", field: "itemHrchyNm", width: 140 },
    { headerName: "itemCd", field: "itemCd", width: 140, cellClass: 'stringType' },
    { headerName: "itemName", field: "itemNm", width: 140 }
  ];

  const [gridLoading, setGridLoading] = useState(false);
  const [p13nLoading, setP13nLoading] = useState(false);

  const getRequestParams = () => {
    const params = {};

    if (filterValue?.locations) {
      params.locCd = filterValue?.locations?.map(location => location?.locCd);
    }

    if (!isEmpty(mpVersionFilter)) {
      params.versionId = mpVersionFilter.id;
      const timeBuckets = mpVersionFilter?.planConfigObj?.TIME_BUCKETS;
      params.timeBuckets = !isEmpty(timeBuckets) ? JSON.stringify(timeBuckets) : null;
    }

    if (filterValue.category) {
      params.itemHrchyCd = filterValue.category.hrchyCd;
    }

    if (extraItemCodesRef.current?.length > 0) {
      params.itemCds = [itemSearch.current, ...extraItemCodesRef.current].filter(Boolean);
    } else if (itemSearch.current) {
      params.itemCd = itemSearch.current;
    }
    return params;
  }

  /*
   * 예외 건수 셀 → 상세. 대상은 **버전 상태에 따라 갈린다**(RELEASED=MP 계획검토 / 그 외=MP PSI 시뮬레이션)
   *   — 지금 열리는 그 화면 하나만 권한을 본다. 둘을 묶어서 보면 쓸 수 있는 링크까지 사라진다.
   */
  const drillTargetKey = () => (mpVersionFilter?.statusCd === RELEASED ? "MP_PLAN_REVIEW" : "MP_PSI_SIMULATION");

  const exceptionCellStyle = params => {
    const { value } = params;
    if (value && value > 0 && isPermitted(drillTargetKey())) {
      return { color: 'blue', textDecoration: 'underline', cursor: 'pointer' };
    }
    return {};
  }

  const onExceptionClicked = params => {
    const { node, data, value } = params;
    if (value && value > 0 && isPermitted(drillTargetKey())) {
      /*
       * 이동 대상 화면(MP PSI Simulation / MP Review)은 itemSearch 를 그대로 API 의 item 파라미터로 보내고,
       * 백엔드는 SearchService.getSearchItemCodes() 로 Elasticsearch 에서 **품목코드** 목록을 만들어
       * item_cd IN (...) 으로 건다. 품목명을 넘기면 ES 색인이 비었거나 장애일 때 폴백이
       * findItemCdContainingItemCd(= item_cd LIKE) 라서 절대 안 맞고 조용히 0건이 된다. → 반드시 itemCd.
       * itemTypeSearch 를 안 넘기면 MP PSI Simulation 이 FG 로 기본값을 잡아 SFG/KIT 예외가 통째로 가려진다.
       * (같은 드릴다운 정본: ResourceGantt.handleGoToPsi)
       */
      const reduxItemValue = {
        itemSearch: "", extraItemCodes: [], mpVersion: mpVersionFilter,
        itemTypeSearch: null, currentPage: 1, appliedExceptions: [], subGridResourceListFilter: []
      };

      if (data) { // Location - Category - Item
        const { itemHrchyCd, itemHrchyNm, itemCd, locCd, locNm } = data;
        let categoryObj = [{ hrchyCd: itemHrchyCd, name: itemHrchyNm, leafFlg: true }];
        if (mpVersionFilter?.statusCd === RELEASED) {
          categoryObj = { hrchyCd: itemHrchyCd, name: itemHrchyNm, leafFlg: true };
        }

        reduxItemValue.filter = { category: categoryObj, locations: [{ locCd, name: locNm }]};
        reduxItemValue.itemSearch = itemCd;
        reduxItemValue.onlyExceptionApplied = true;
      } else {  // Location or Location - Category
        const { field, allLeafChildren } = node;
        const baseChildren = allLeafChildren[0].data;
        if (isEqual(field, "itemHrchyNm")) {
          let categoryObj = [{ hrchyCd: baseChildren.itemHrchyCd, name: baseChildren.itemHrchyNm, leafFlg: true }];
          if (mpVersionFilter?.statusCd === RELEASED) {
            categoryObj = { hrchyCd: baseChildren.itemHrchyCd, name: baseChildren.itemHrchyNm, leafFlg: true };
          }

          reduxItemValue.filter = { category: categoryObj};
        }
        reduxItemValue.filter = { ...reduxItemValue.filter, locations: [{locCd: baseChildren?.locCd, name: baseChildren?.locNm}] };
        reduxItemValue.onlyExceptionApplied = true;
      }

      /*
       * Version Status에 따라 Link되는 화면 변경
       * Release: MP Reveiew
       * Simulation: MP PSI Simulation
       **/

      let reduxKey = "MP_PSI_SIMULATION";
      if (mpVersionFilter?.statusCd === RELEASED) {
        reduxKey = "MP_PLAN_REVIEW";
      }

      /* 이전 기존 redux 제거 */
      const removeState = reduxUtil.getViewState(reduxKey);
      reduxDispatch(reduxUtil.removeViewState(removeState));

      const reduxState = reduxUtil.createObjectState(reduxKey, { ...reduxItemValue });

      reduxDispatch(reduxUtil.updateViewState(reduxState));
      reduxDispatch(reduxUtil.updateHistoryState(reduxKey));
    }
  }

  const setColumnByDimensionData = (colDefs) => {
    const requiredCols = requiredDimensions.concat(dimensions);

    if (dimensions.indexOf("itemHrchyNmGrp") > -1) {
      requiredCols.push("itemHrchyNm");
    }

    colDefs = colDefs.filter(columnDefs => {
      if (Object.keys(columnDefs).includes('colId')) {
        return requiredCols.indexOf(columnDefs.colId) > -1;
      } else {
        return requiredCols.indexOf(columnDefs.field) > -1;
      }
    });

    return colDefs;
  }

  const setGridHeaders = (headers, headerMap) => {
    if (headers.length <= 0) {
      return;
    }

    const colDefs = setColumnByDimensionData(cloneDeep(defaultColumnDefs));
    /* 이전 Column의 Status를 기억하기 때문에 ColumnDefs의 순서를 따라가지 않음에 따라 UK 설정 */
    const bucketPostFix = mpVersionFilter?.id ? GridUtils.IGNORE_P13N_COLLID + mpVersionFilter.id : GridUtils.IGNORE_P13N_COLLID;
    bucketColumnDefs.current = [];

    const defaultNumColDef = { width: 110, type: ["numberColumn", "nonEditableColumn", "rightAligned"], aggFunc: 'sum' };
    headers.forEach((header) => {
      const colDef = {
        headerName: headerMap[header], field: header,
        colId: header + bucketPostFix,
        ...defaultNumColDef,
        onCellClicked: (params) => onExceptionClicked({ ...params }),
        cellStyle: exceptionCellStyle
      };
      if (reduxViewState?.baseDate && isEqual(header, reduxViewState.baseDate)) {
        colDef.sort = "desc";
      }
      bucketColumnDefs.current.push(colDef);
    });

    bucketColumnDefs.current.push({
      headerName: "total", field: "exceptions",
      colId: 'total' + bucketPostFix,
       ...defaultNumColDef
    });

    columnDefs.current = [...colDefs, ...bucketColumnDefs.current];

    if (gridLoading) {
      // 차원 설정으로 껐던 컬럼이 되살아나면 맨 뒤로 밀리므로 columnDefs 순서를 지켜준다
      GridUtils.setColumnDefs({ ...gridRef.current, columnDefs: [...colDefs, ...bucketColumnDefs.current], alignOrderToDefs: true });

      const reduxState = reduxUtil.createObjectState(viewName, { gridColumns : colDefs.map(columnDefs => {
        if (Object.keys(columnDefs).includes('colId')) {
          return columnDefs.colId;
        } else {
          return columnDefs.field;
        }
      })});
      reduxDispatch(reduxUtil.updateViewState({ ...reduxState }));
    }
  }

  const setGridDatas = (headers, results) => {
    const datas = [];
    const exceptionPivotData = "exceptionPivotData";

    for (const resultsData of results) {
      for (let idx = 0, len = headers.length; idx < len; idx++) {
        const header = headers[idx];
        resultsData[header] = resultsData[exceptionPivotData][idx];
      }

      datas.push(resultsData);
    }

    return datas;
  }

  const retrieveExceptionsDetail = () => {
    gridRef.current?.api?.setGridOption('loading', true);

    const params = getRequestParams();
    const colDefs = setColumnByDimensionData(cloneDeep(defaultColumnDefs));
    productionExceptionsService.getAllDetail(params)
      .then(response => {
        const { pivotResult } = response.data;
        const { localDateHeader, headerMap, data } = pivotResult;
        if (data && !isEmpty(data)) {
          setGridHeaders(localDateHeader, headerMap);
          setInvExceptionsDetail(setGridDatas(localDateHeader, data));
        } else {
          setSnackInfo({ open: true, severity: snackBarMessageColor.dataEmpty, content: t("MSG_NoData") });

          GridUtils.setColumnDefs({ ...gridRef.current, columnDefs: colDefs, alignOrderToDefs: true });
          setInvExceptionsDetail([]);
        }
      })
      .catch(e => {
        GridUtils.setColumnDefs({ ...gridRef.current, columnDefs: colDefs, alignOrderToDefs: true });
        debug(e);
      })
      .then(() => {
        const reduxState = reduxUtil.createObjectState(viewName, { itemSearch: itemSearch.current, extraItemCodes: extraItemCodesRef.current, filter: filterValue });
        reduxDispatch(reduxUtil.updateViewState(reduxState));

        gridRef.current?.api?.setGridOption('loading', false);
      });
  }

  const onFirstDataRendered = () => {
    if (reduxViewState?.baseDate) {
      const header = reduxViewState.baseDate;
      const nodes = [];

      gridRef.current.api.forEachNode(node => {
        const { field, aggData } = node;
        if (isEqual(field, "categoryNm") && !isNull(aggData[header])) {
          nodes.push(node);
        }
      });
      gridRef.current.api.flashCells({ rowNodes: nodes, columns: [header], flashDelay: 2000, fadeDelay: 1000 });

      const reduxState = reduxUtil.createObjectState(viewName, { baseDate: null });
      reduxDispatch(reduxUtil.updateViewState(reduxState));
    }
  }

  const onGridReady = (params) => {
    setGridLoading(true);
    GridUtils.setColumnDefs({ ...params, columnDefs: defaultColumnDefs, viewName, initState: true});
  }

  useEffect(() => {
    if (gridLoading && !isEmpty(mpVersionFilter) && p13nLoading) {
      retrieveExceptionsDetail();
    } else {
      setInvExceptionsDetail([]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterValue, dimensions, mpVersionFilter?.id]);

  const applyDimensions = selectedDimensions => {
    setP13nLoading(true);
    setDimensions(selectedDimensions);
  }

  return (
    <>
      <FilterContainer>
        <MpVersionFilter viewName={viewName} mpVersionValue={mpVersionFilter} onChangeFilter={setMpVersionFilter} statusFilters={[PLANNED, RELEASED]} />
        <MPLocationTreeFilter viewName={viewName} filterValue={filterValue?.locations?.map(location => location?.locCd)} onChangeFilter={setFilterValue} />
        <ItemHrchyTreeFilter viewName={viewName} onChangeFilter={setFilterValue} label={"itemHrchy"} styles={{ width: 250 }} />
        <MPItemAutocomplete retrieve={retrieveExceptionsDetail} sx={{ width: 250 }} searchValue={itemSearch} key={"ITEM"} extraCodesRef={extraItemCodesRef} />
        <Box sx={{ ml: "auto" }}>
          {/* setter 를 그대로 넘기면 클릭 **이벤트 객체**가 state 에 들어간다(MUI Dialog 의 open 은 boolean) */}
          <SettingsButton label="dimensionSetting" size="medium" onClick={() => setDimensionModalOpen(true)} />
          <ExcelExportButton title={title} service={productionExceptionsService} columnDefs={[...defaultExportColumnDefs, ...bucketColumnDefs.current]}
            setExcelData={setGridDatas}
            params={getRequestParams()} />
        </Box>
      </FilterContainer>
      <Box className={"ag-theme-balham grid-area"} sx={{ pb: 1 }}>
        <AgGridReact
          ref={gridRef}
          rowData={invExceptionsDetail}
          {...defaultGridMemo}
          groupDisplayType={'custom'}
          animateRows
          suppressAggFuncInHeader
          groupDefaultExpanded={dimensions.length}
          onGridReady={onGridReady}
          onFirstDataRendered={onFirstDataRendered}
        />
      </Box>
      <Snackbar
        open={snackInfo.open}
        onClose={() => setSnackInfo({ ...snackInfo, open: false })}
        severity={snackInfo.severity}
        content={snackInfo.content}
      />
      <DimensionModal
        open={dimensionModalOpen}
        setOpen={setDimensionModalOpen}
        viewName={viewName}
        allDimensions={allDimensions}
        applyDimensions={applyDimensions}
        headerNameByField={headerNameByField}
      />
    </>
  )
};

export default withTranslation()(ProductionExceptionsDetail);
